import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import CurrencyPicker from '../components/ui/CurrencyPicker';
import { uiStyles } from '../theme/uiStyles';
import { slugify } from '../utils/slugify';
import { apiFetch } from '../api/apiClient';

const BENCHMARKS = [
  { ticker: 'SPY',  label: 'S&P 500 (SPY)' },
  { ticker: 'QQQ',  label: 'NASDAQ 100 (QQQ)' },
  { ticker: 'VT',   label: 'Total World (VT)' },
  { ticker: 'VEA',  label: 'Developed ex-US (VEA)' },
  { ticker: 'BTC-USD', label: 'Bitcoin (BTC)' },
];

export default function PortfolioCreatePage() {
  const navigate = useNavigate();
  // Note: previously we read user/isFree here to gate benchmark + wallet —
  // those gates are now removed (no public Free/Paid tier yet).

  const [name, setName]                 = useState('');
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [color, setColor]               = useState('#6366f1');
  const [benchmarkTicker, setBenchmarkTicker] = useState('SPY');
  const [returnMethod, setReturnMethod] = useState('TWR');
  const [costBasisMethod, setCostBasisMethod] = useState('Average');
  const [dividendMode, setDividendMode] = useState('Cash');
  const [includeDividends, setIncludeDividends] = useState(true);
  const [includeCash, setIncludeCash]   = useState(true);

  const [creating, setCreating] = useState(false);
  const [error, setError]       = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!name.trim()) { setError('Please enter a portfolio name.'); return; }

    setCreating(true);
    try {
      const payload = {
        name: name.trim(),
        baseCurrency,
        includeCashInPerformance: includeCash,
        includeDividendsInReturn: includeDividends,
        returnCalculationMethod: returnMethod,
        dividendMode,
        costBasisMethod,
        defaultChartRange: '1mo',
        benchmarkTicker,
      };

      const res = await apiFetch('/api/portfolios', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error('Failed to create portfolio. ' + text);
      }

      const created = await res.json();
      // Persist the chosen accent colour locally (one per portfolio id)
      if (created?.id != null) {
        try { localStorage.setItem(`portfolioColor-${created.id}`, color); } catch {}
      }

      navigate('/portfolio/' + slugify(name.trim()));
    } catch (err) {
      setError(err.message || 'Failed to create portfolio.');
      setCreating(false);
    }
  }

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>New portfolio</h1>
      <p style={s.sub}>Set up your portfolio. You can change everything later in settings.</p>

      {error && <div style={uiStyles.errorBox}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <section style={s.card}>
          <h2 style={s.h2}>Identity</h2>

          <label style={s.label}>Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Long-term, Crypto, Cash flow…"
            style={uiStyles.input}
            autoFocus
          />

          <label style={{ ...s.label, marginTop: '16px' }}>Accent color</label>
          <div style={s.colorRow}>
            <label style={{ ...s.colorSwatch, background: color }}>
              <input
                type="color"
                value={color}
                onChange={e => setColor(e.target.value)}
                style={s.hiddenColorInput}
                aria-label="Pick accent color"
              />
            </label>
            <span style={s.colorHex}>{color.toUpperCase()}</span>
          </div>
        </section>

        <section style={s.card}>
          <h2 style={s.h2}>Currency & benchmark</h2>

          <label style={s.label}>Base currency</label>
          <CurrencyPicker value={baseCurrency} onChange={setBaseCurrency} disabled={creating} />

          <label style={{ ...s.label, marginTop: '16px' }}>Benchmark</label>
          <select
            value={benchmarkTicker}
            onChange={e => setBenchmarkTicker(e.target.value)}
            style={uiStyles.input}
          >
            {BENCHMARKS.map(b => <option key={b.ticker} value={b.ticker}>{b.label}</option>)}
          </select>
        </section>

        <section style={s.card}>
          <h2 style={s.h2}>Calculation</h2>

          <label style={s.label}>Return method</label>
          <select value={returnMethod} onChange={e => setReturnMethod(e.target.value)} style={uiStyles.input}>
            <option value="TWR">Time-Weighted Return (TWR)</option>
            <option value="MWR">Money-Weighted Return (MWR / IRR)</option>
          </select>

          <label style={{ ...s.label, marginTop: '16px' }}>Cost basis</label>
          <select value={costBasisMethod} onChange={e => setCostBasisMethod(e.target.value)} style={uiStyles.input}>
            <option value="Average">Average cost</option>
            <option value="FIFO">FIFO</option>
            <option value="LIFO">LIFO</option>
          </select>

          <label style={{ ...s.label, marginTop: '16px' }}>Dividend handling</label>
          <select value={dividendMode} onChange={e => setDividendMode(e.target.value)} style={uiStyles.input}>
            <option value="Cash">Cash (collected as cash)</option>
            <option value="Reinvest">Reinvested (DRIP)</option>
          </select>

          <label style={s.toggleRow}>
            <input type="checkbox" checked={includeDividends} onChange={e => setIncludeDividends(e.target.checked)} />
            <span>Include dividends in return</span>
          </label>

          <label style={s.toggleRow}>
            <input type="checkbox" checked={includeCash} onChange={e => setIncludeCash(e.target.checked)} />
            <span>Include cash in performance</span>
          </label>
        </section>

        <div style={s.actions}>
          <Link to="/portfolios" style={s.cancelBtn}>Cancel</Link>
          <button type="submit" style={uiStyles.primaryButton} disabled={creating}>
            {creating ? 'Creating…' : 'Create portfolio'}
          </button>
        </div>
      </form>
    </div>
  );
}

const s = {
  wrap:        { maxWidth: '900px', margin: '0 auto', padding: '20px 0 40px', color: 'var(--text-primary)' },
  back:        { display: 'inline-block', color: 'var(--accent)', textDecoration: 'none', fontSize: '14px', fontWeight: 600, marginBottom: '12px' },
  h1:          { fontSize: '28px', fontWeight: 700, margin: '0 0 4px' },
  sub:         { color: 'var(--text-secondary)', fontSize: '14px', margin: '0 0 24px' },
  card:        { background: 'var(--surface-strong)', border: '1px solid var(--border)', borderRadius: '18px', padding: '24px', marginBottom: '20px', boxShadow: 'var(--shadow)' },
  h2:          { fontSize: '17px', fontWeight: 700, margin: '0 0 14px' },
  label:       { display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' },
  colorRow:    { display: 'flex', gap: '12px', alignItems: 'center' },
  colorSwatch: { width: '52px', height: '40px', borderRadius: '10px', cursor: 'pointer', display: 'inline-block', position: 'relative', overflow: 'hidden' },
  hiddenColorInput: { position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', border: 'none', padding: 0, cursor: 'pointer' },
  colorHex:    { fontFamily: 'monospace', fontSize: '13px', color: 'var(--text-secondary)' },
  toggleRow:   { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginTop: '12px', cursor: 'pointer' },
  actions:     { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' },
  cancelBtn:   { display: 'inline-flex', alignItems: 'center', padding: '10px 16px', borderRadius: '10px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px', textDecoration: 'none', cursor: 'pointer' },
};
